import {
  createContext,
  Dispatch,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";

import {
  AttributeKey,
  TabId,
  TabSectionKey,
} from "../services/api/types/portalConfiguration/translations";
import { DefaultTranslations } from "../utils/seedDefaultTranslation";

type LoadStatus = "idle" | "loading" | "loaded" | "error";

export type Translations = {
  [K in TabId]: {
    [S in TabSectionKey]?: {
      [A in AttributeKey]?: string;
    };
  };
};

export type TranslationState = {
  translations: Translations;
  loadStatus: LoadStatus;
};

const initialTranslations = DefaultTranslations as unknown as Translations;
type TranslationAction =
  | {
      type: "UPDATE_TAB_SECTION";
      payload: {
        tabId: TabId;
        tabSectionKey: TabSectionKey;
        updatedAttributes: Partial<Record<AttributeKey, string>>;
      };
    }
  | {
      type: "SET_ALL";
      payload: Partial<Translations>;
    }
  | {
      type: "SET_LOAD_STATUS";
      payload: LoadStatus;
    };

const TranslationReducer = (
  state: TranslationState,
  action: TranslationAction,
): TranslationState => {
  switch (action.type) {
    case "UPDATE_TAB_SECTION": {
      const { tabId, tabSectionKey, updatedAttributes } = action.payload;
      const tab = state.translations[tabId] ?? {};
      const section = tab[tabSectionKey] ?? {};

      return {
        ...state,
        translations: {
          ...state.translations,
          [tabId]: {
            ...tab,
            [tabSectionKey]: {
              ...section,
              ...updatedAttributes,
            },
          },
        },
      };
    }

    case "SET_ALL": {
      return {
        ...state,
        translations: {
          ...state.translations,
          ...action.payload,
        } as Translations,
      };
    }

    case "SET_LOAD_STATUS": {
      return {
        ...state,
        loadStatus: action.payload,
      };
    }

    default:
      return state;
  }
};

interface TranslationContextType {
  translations: TranslationState;
  dispatch: Dispatch<TranslationAction>;
  updateTranslationSection: (
    tabId: TabId,
    tabSectionKey: TabSectionKey,
    updatedAttributes: Partial<Record<AttributeKey, string>>,
  ) => void;
}

const TranslationContext = createContext<TranslationContextType>({
  translations: { translations: initialTranslations, loadStatus: "idle" },
  dispatch: () => {},
  updateTranslationSection: () => {},
});

export const TranslationProvider = ({ children }: { children: ReactNode }) => {
  const [translations, dispatch] = useReducer(TranslationReducer, {
    translations: initialTranslations,
    loadStatus: "idle",
  });

  const updateTranslationSection = useCallback(
    (
      tabId: TabId,
      tabSectionKey: TabSectionKey,
      updatedAttributes: Partial<Record<AttributeKey, string>>,
    ) => {
      dispatch({
        type: "UPDATE_TAB_SECTION",
        payload: { tabId, tabSectionKey, updatedAttributes },
      });
    },
    [],
  );

  const memoizedValue = useMemo(
    () => ({
      translations,
      dispatch,
      updateTranslationSection,
    }),
    [translations, dispatch, updateTranslationSection],
  );

  return (
    <TranslationContext.Provider value={memoizedValue}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => useContext(TranslationContext);
